import { useEffect, useState } from 'react';

import { useConfirmCfAccountMutation } from '../api/cloudflareAccountConfirmationApi';
import { httpStatusOf } from '../api/httpStatusOf';

const BAD_REQUEST = 400;
const NOT_FOUND = 404;
const TOO_MANY_REQUESTS = 429;

export type CfAccountConfirmationFailure = 'domain_rejected' | 'no_flow' | 'throttled' | 'server_error' | 'request_failed';

export type CfAccountConfirmation = {
  /** Resolves `true` once the server has confirmed the account. */
  confirm: () => Promise<boolean>;
  isConfirming: boolean;
  failure: CfAccountConfirmationFailure | null;
};

const failureOf = (error: unknown): CfAccountConfirmationFailure => {
  const status = httpStatusOf(error);

  if (status === undefined) return 'request_failed';
  if (status === BAD_REQUEST) return 'domain_rejected';
  if (status === NOT_FOUND) return 'no_flow';
  if (status === TOO_MANY_REQUESTS) return 'throttled';

  return 'server_error';
};

export const useCfAccountConfirmation = (domain: string, onTheAccountStep: boolean): CfAccountConfirmation => {
  const [confirmCfAccount] = useConfirmCfAccountMutation();
  const [isConfirming, setIsConfirming] = useState(false);
  const [failure, setFailure] = useState<CfAccountConfirmationFailure | null>(null);

  useEffect(() => {
    if (!onTheAccountStep) setFailure(null);
  }, [onTheAccountStep]);

  const confirm = async (): Promise<boolean> => {
    if (isConfirming) return false;

    setIsConfirming(true);
    try {
      await confirmCfAccount({ domain }).unwrap();
      setFailure(null);

      return true;
    } catch (error) {
      setFailure(failureOf(error));

      return false;
    } finally {
      setIsConfirming(false);
    }
  };

  return { confirm, isConfirming, failure };
};
